/**
 * Cross-panel selection.
 *
 * One selection at a time is shared by the cue sheet of the project page, the
 * My Studio timeline and the lists of the left panel, so that whatever is
 * selected can drive both the bulk actions and the scope of the Comm Center
 * feed. Keeping it in a single store value is what lets the sidebar react to a
 * selection made three components away without threading callbacks through the
 * whole tree.
 *
 * `kind` says what the ids refer to — 'cue' (cue sheet item / timeline track),
 * 'video' (project video) or 'rec' (rec session) — so a consumer never has to
 * guess which list an id belongs to. Selecting in one list replaces whatever
 * was selected in another: two kinds at once have no meaning for the actions
 * built on top of it.
 */

export const SELECTION_KINDS = {
    CUE: 'cue',
    VIDEO: 'video',
    REC: 'rec',
};

export const EMPTY_SELECTION = { kind: null, ids: [], anchor: null };

/** True when the ctrl / cmd modifier of a click asks for an additive toggle. */
export const isMultiSelectEvent = (event) => Boolean(event?.metaKey || event?.ctrlKey);

/** True when the click asks for a range, i.e. shift without ctrl / cmd. */
export const isRangeSelectEvent = (event) => Boolean(event?.shiftKey) && !isMultiSelectEvent(event);

export const getSelectedIds = (selection, kind) => (
    selection?.kind === kind ? (selection.ids || []) : []
);

export const isItemSelected = (selection, kind, id) => (
    getSelectedIds(selection, kind).includes(id)
);

const range = (orderedIds, from, to) => {
    const start = orderedIds.indexOf(from);
    const end = orderedIds.indexOf(to);

    if (start === -1 || end === -1) return [to];

    return orderedIds.slice(Math.min(start, end), Math.max(start, end) + 1);
};

/**
 * The selection a click produces.
 *
 * @param {object} selection   current selection
 * @param {object} params
 * @param {string} params.kind       what the id refers to
 * @param {string} params.id         item that was clicked
 * @param {array}  params.orderedIds ids in display order, for a shift range
 * @param {Event}  params.event      the click, read for its modifiers
 * @returns {object} the next selection
 */
export const nextSelection = (selection, { kind, id, orderedIds = [], event = null }) => {
    const current = selection?.kind === kind ? selection : EMPTY_SELECTION;
    const ids = current.ids || [];

    if (isRangeSelectEvent(event) && current.anchor) {
        return { kind, ids: range(orderedIds, current.anchor, id), anchor: current.anchor };
    }

    if (isMultiSelectEvent(event)) {
        const next = ids.includes(id) ? ids.filter(selected => selected !== id) : [...ids, id];

        return next.length
            ? { kind, ids: next, anchor: id }
            : EMPTY_SELECTION;
    }

    // A plain click on the only selected item clears the selection, which is
    // how a row of the cue sheet has always behaved.
    if (ids.length === 1 && ids[0] === id) {
        return EMPTY_SELECTION;
    }

    return { kind, ids: [id], anchor: id };
};

/** Drops from the selection the ids that no longer exist. */
export const pruneSelection = (selection, kind, existingIds) => {
    if (selection?.kind !== kind) return selection;

    const ids = (selection.ids || []).filter(id => existingIds.includes(id));

    if (ids.length === (selection.ids || []).length) return selection;

    return ids.length
        ? { ...selection, ids, anchor: ids.includes(selection.anchor) ? selection.anchor : ids[ids.length - 1] }
        : EMPTY_SELECTION;
};
